import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Supabase環境変数が設定されていません');
}

/**
 * クロスタブでのロック競合によるフリーズを防ぐカスタムロック。
 * Navigator LockManager にタイムアウト上限(10秒)を設け、
 * ロックが取得できない場合でもデッドロックにならないようにする。
 */
const lockWithTimeout = async <R>(
  name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>
): Promise<R> => {
  if (typeof navigator === 'undefined' || !navigator.locks) {
    return fn();
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 10000);

  try {
    return await navigator.locks.request(
      name,
      { mode: 'exclusive', signal: controller.signal },
      async () => fn()
    );
  } catch {
    // ロック取得に失敗/タイムアウトした場合はロック無しで実行してデッドロックを回避
    return fn();
  } finally {
    clearTimeout(timer);
  }
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // トークンを自動更新する（これが無効だと期限切れトークンで
    // プロフィール取得やEdge Function呼び出しが401で失敗し、
    // ログインできない・管理操作ができない原因になる）
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
    lock: lockWithTimeout,
  },
});

// 型定義
export interface Profile {
  id: string;
  username: string;
  furigana: string;
  email: string;
  role: 'super_admin' | 'admin' | 'user';
  phone: string | null;
  office_id: string | null;
  departments: string[] | null;
  created_at: string;
  updated_at: string;
}

export interface Office {
  id: string;
  name: string;
  created_at: string;
}

export interface Announcement {
  id: string;
  title: string;
  content: string;
  target_role: 'all' | 'admin' | 'user';
  office_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface SupportRecord {
  id: string;
  user_id: string;
  title: string;
  content: string;
  status: 'pending' | 'in_progress' | 'resolved';
  priority: 'low' | 'medium' | 'high';
  office_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface BusinessCard {
  id: string;
  name: string;
  furigana: string | null;
  company_name: string | null;
  position: string | null;
  department: string | null;
  office_phone: string | null;
  mobile_phone: string | null;
  fax: string | null;
  website: string | null;
  email: string | null;
  address: string | null;
  card_image_url: string | null;
  staff_in_charge: string | null;
  contact_date: string | null;
  notes: string | null;
  created_by: string;
  office_id: string | null;
  created_at: string;
  updated_at: string;
}

// リトライ機能付きクエリ実行（改善版）
export async function queryWithRetry<T>(
  queryFn: () => Promise<{ data: T | null; error: any }>,
  maxRetries = 2,
  baseDelay = 500
): Promise<{ data: T | null; error: any }> {
  let lastError;

  for (let i = 0; i < maxRetries; i++) {
    try {
      const result = await queryFn();

      if (!result.error) {
        return result;
      }

      lastError = result.error;

      // 認証エラーや権限エラーの場合はリトライしない
      if (
        result.error.code === 'PGRST301' ||
        result.error.message?.includes('JWT') ||
        result.error.message?.includes('permission')
      ) {
        return result;
      }

      // 最後の試行でない場合は待機
      if (i < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, baseDelay * Math.pow(2, i)));
      }
    } catch (error: any) {
      lastError = error;

      if (error.name === 'AbortError') {
        console.error('クエリタイムアウト');
        return { data: null, error: new Error('リクエストがタイムアウトしました') };
      }

      if (i < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, baseDelay * Math.pow(2, i)));
      }
    }
  }

  return { data: null, error: lastError };
}

// 接続状態チェック
export async function checkConnection(): Promise<boolean> {
  try {
    const { error } = await supabase.from('profiles').select('id').limit(1);
    return !error;
  } catch {
    return false;
  }
}